import type { Exercise } from '@fitness/api-client';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Pencil, TrendingUp, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/exercise/Chip';
import { ExerciseForm } from '@/components/exercise/ExerciseForm';
import { ExerciseMedia } from '@/components/exercise/ExerciseMedia';
import { CrossLink } from '@/components/routine/CrossLink';
import { describeError, hasErrorCode } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  categoryLabel,
  equipmentLabel,
  forceLabel,
  levelLabel,
  mechanicLabel,
  muscleLabel,
} from '@/lib/i18n/training';
import { useDeleteExercise, useExercise, useUpdateExercise } from '@/lib/queries/exercises';

export const Route = createFileRoute('/_app/exercises/$id')({
  component: ExerciseDetailPage,
});

function ExerciseDetailPage() {
  const { id } = Route.useParams();
  const query = useExercise(id);

  return (
    <>
      <PageHeader
        title={query.data?.nameEs ?? 'Ejercicio'}
        {...(query.data && query.data.nameEn !== query.data.nameEs
          ? { subtitle: query.data.nameEn }
          : {})}
        action={
          <Link
            to="/exercises"
            search={{}}
            aria-label="Volver al catálogo"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {query.isPending ? (
        <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface-raised p-4 text-ink-muted">
          <Spinner size="sm" />
          <span className="text-sm">Cargando el ejercicio…</span>
        </div>
      ) : query.isError ? (
        <ErrorBanner
          title="No hemos podido cargar el ejercicio"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <ExerciseDetail exercise={query.data} />
      )}
    </>
  );
}

function ExerciseDetail({ exercise }: { exercise: Exercise }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const isMine = exercise.ownerId !== null && exercise.ownerId === user?.id;

  const steps =
    exercise.instructionsEs.length > 0 ? exercise.instructionsEs : exercise.instructionsEn;
  const stepsInEnglish = exercise.instructionsEs.length === 0 && exercise.instructionsEn.length > 0;

  if (editing) {
    return <ExerciseEditor exercise={exercise} onDone={() => setEditing(false)} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <ExerciseMedia media={exercise.media} name={exercise.nameEs} />

      <div className="flex flex-wrap gap-1.5">
        <Badge tone="brand">{muscleLabel(exercise.primaryMuscle)}</Badge>
        {exercise.secondaryMuscles.map((muscle) => (
          <Badge key={muscle}>{muscleLabel(muscle)}</Badge>
        ))}
        <Badge>{equipmentLabel(exercise.equipment)}</Badge>
        <Badge>{categoryLabel(exercise.category)}</Badge>
        {exercise.level !== null ? <Badge>{levelLabel(exercise.level)}</Badge> : null}
        {exercise.mechanic !== null ? <Badge>{mechanicLabel(exercise.mechanic)}</Badge> : null}
        {exercise.force !== null ? <Badge>{forceLabel(exercise.force)}</Badge> : null}
        {exercise.unilateral ? <Badge tone="brand">Unilateral · repes por lado</Badge> : null}
      </div>

      <section aria-labelledby="instrucciones">
        <h2 id="instrucciones" className="mb-2 text-base font-semibold">
          Cómo se hace
        </h2>
        {steps.length === 0 ? (
          <p className="text-sm text-ink-muted">Este ejercicio no tiene instrucciones todavía.</p>
        ) : (
          <>
            {stepsInEnglish ? (
              <p className="mb-2 text-xs text-ink-faint">Instrucciones en inglés.</p>
            ) : null}
            <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-ink-muted marker:text-brand">
              {steps.map((step, index) => (
                <li key={`${index}-${step.slice(0, 12)}`}>{step}</li>
              ))}
            </ol>
          </>
        )}
      </section>

      <CrossLink
        to="/progress/exercises/$id"
        params={{ id: exercise.id }}
        className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 transition-colors hover:bg-surface-overlay"
      >
        <TrendingUp className="size-5 text-brand" aria-hidden="true" />
        <span className="text-sm font-medium text-ink">Ver progreso en este ejercicio</span>
      </CrossLink>

      {isMine ? <OwnerActions exercise={exercise} onEdit={() => setEditing(true)} /> : null}
    </div>
  );
}

function ExerciseEditor({ exercise, onDone }: { exercise: Exercise; onDone: () => void }) {
  const update = useUpdateExercise(exercise.id);

  return (
    <ExerciseForm
      exercise={exercise}
      submitting={update.isPending}
      errorMessage={update.isError ? describeError(update.error, { saving: true }) : undefined}
      errorTitle="No se han podido guardar los cambios"
      submitLabel="Guardar cambios"
      onCancel={onDone}
      onSubmit={(input) => {
        update.mutate(input, { onSuccess: onDone });
      }}
    />
  );
}

/** Editar y borrar, sólo para los ejercicios que ha creado el usuario. */
function OwnerActions({ exercise, onEdit }: { exercise: Exercise; onEdit: () => void }) {
  const navigate = useNavigate();
  const remove = useDeleteExercise(exercise.id);
  const [confirming, setConfirming] = useState(false);

  const inUse = remove.isError && hasErrorCode(remove.error, 'exercise_in_use');

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-4">
      <p className="text-xs text-ink-faint">Este ejercicio es tuyo.</p>
      <div className="flex gap-2">
        <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={onEdit}>
          Editar
        </Button>
        {confirming ? null : (
          <Button
            variant="ghost"
            icon={<Trash2 className="size-4" />}
            onClick={() => setConfirming(true)}
          >
            Eliminar
          </Button>
        )}
      </div>

      {confirming ? (
        <div className="rounded-xl border border-danger/40 bg-danger-surface p-3">
          <p className="text-sm">
            ¿Seguro que quieres eliminar <strong>{exercise.nameEs}</strong>?
          </p>
          {remove.isError ? (
            <p role="alert" className="mt-2 text-sm text-danger">
              {inUse
                ? 'Este ejercicio está en rutinas o entrenamientos; no se puede borrar.'
                : describeError(remove.error)}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(undefined, {
                  onSuccess: () => {
                    void navigate({ to: '/exercises', search: {} });
                  },
                })
              }
            >
              Sí, eliminar
            </Button>
            <Button
              variant="secondary"
              disabled={remove.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
